import { KeyedObject } from 'bernie-core/dist/source';
import { getBexApiContext } from 'bernie-context';
import { ContextStore, FetchOptions, SerializedData, timeoutPromise } from 'bernie-core';
import { BexApiStore, BernieGraphqlClient, ClientOptions } from 'bernie-graphql';
import { getErrorString, Logger } from 'bernie-logger';
import { action, observable, runInAction } from 'mobx';
import { ClientLogger } from 'bernie-client';
import { ContextInput, FetchShortlisted } from '../common/__generated__/api';
import { fetchWithRetry } from '../helpers/operations-helper';
import { ApiOperations } from './../common/__generated__/api/api-operations';
import { STORE_EVENTS } from './store-events';

const NUM_OF_RETRIES = 1;
const TIMEOUT = 5000;

export class ShortlistStore extends BexApiStore {
  @observable public shortlistedActivityIds?: string[];
  @observable public showNotification?: boolean;
  public shortlistedActivityName?: string;

  private context?: ContextInput;

  public constructor(
    client: (client: BernieGraphqlClient) => ApiOperations,
    bexConfigs: ClientOptions,
    logger: Logger | ClientLogger
  ) {
    super(client, bexConfigs, logger);
    this.logger = logger;
  }

  public hydrate(data: SerializedData): void {
    super.hydrate(data);
    this.bexApiSource.clientConfigs.bexApiUrl = '/graphql';
  }

  public setContext(context: ContextStore): void {
    this.context = getBexApiContext(context);
  }

  @action
  public removeShortlisted(activityId: string): Promise<any> {
    const variables = this.getMutationVariables(activityId);
    this.unsaveActivity(activityId);

    return fetchWithRetry(this.bexApiSource.operations.removeShortlistItem(variables), NUM_OF_RETRIES).catch(
      (error: Error) => {
        this.saveActivity(activityId);
        runInAction('recovering from bex api shortlist remove call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.SHORTLIST_REMOVE_FAILED, variables, getErrorString(error));
        });
      }
    );
  }

  @action
  public addShortlisted(activityId: string): Promise<any> {
    const variables = this.getMutationVariables(activityId);
    this.saveActivity(activityId);

    return fetchWithRetry(this.bexApiSource.operations.saveShortlistItem(variables), NUM_OF_RETRIES).catch(
      (error: Error) => {
        this.unsaveActivity(activityId);
        runInAction('recovering from bex api shortlist remove call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.SHORTLIST_ADD_FAILED, variables, getErrorString(error));
        });
      }
    );
  }

  public fetchShortlist(options: FetchOptions, pageId: string): Promise<void> {
    if (options.isServer) {
      return this.fetchShortlisted(pageId);
    }

    return Promise.resolve();
  }

  @action
  public fetchShortlisted(pageId: string): Promise<void> {
    const searchVariables = this.getQueryVariables();

    return timeoutPromise(
      this.bexApiSource.operations.fetchShortlisted(searchVariables, { pageId }),
      TIMEOUT,
      `store=shortlist fetch=fetchShortlisted timeout`
    )
      .then((response: KeyedObject) => {
        runInAction('update shortlist search from response', () => {
          this.shortlistFetchSuccess(response.data.shortlist);
        });
      })
      .catch((error: Error) => {
        runInAction('recovering from bex api shortlist fetch call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.SHORTLIST_FETCH_FAILED, searchVariables, getErrorString(error));
        });
      });
  }

  public updateAndShowNotification(activityName: string): void {
    this.showNotification = true;
    this.shortlistedActivityName = activityName;
  }

  public hideNotification(): void {
    this.showNotification = false;
    this.shortlistedActivityName = '';
  }

  private shortlistFetchSuccess(shortlist: FetchShortlisted.Shortlist): void {
    this.shortlistedActivityIds =
      (shortlist && shortlist.details && shortlist.details.map(t => t.productId)) || undefined;
  }

  private getMutationVariables(activityId: string): SerializedData {
    return {
      context: this.context,
      productId: activityId
    };
  }

  private getQueryVariables(): SerializedData {
    return {
      context: this.context
    };
  }

  private unsaveActivity(activityId: string): void {
    this.shortlistedActivityIds =
      this.shortlistedActivityIds && this.shortlistedActivityIds.filter(it => it !== activityId);
  }

  private saveActivity(activityId: string): void {
    this.shortlistedActivityIds
      ? this.shortlistedActivityIds.push(activityId)
      : (this.shortlistedActivityIds = [activityId]);
  }
}
